import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, TrendingUp, Users, DollarSign, BarChart3, Download, Pause, Play, Trash2, Edit } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { EnhancedTable } from "./EnhancedTable";
import { BulkActions } from "./BulkActions";
import { SplitPane } from "./SplitPane";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { compressImage } from "@/utils/image-compression";
import { useImageQuality } from "@/hooks/useImageQuality";

interface Ad {
  id: string;
  ad_name?: string;
  company_name: string;
  image_url: string;
  page_placement: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  clicks: number;
  impressions: number;
  ctr: number;
  amount_spent: number;
  estimated_budget?: number;
  cost_per_impression?: number;
  cost_per_click?: number;
  cta_url: string;
  offer_text?: string;
  target_locations?: string[];
  category?: string;
  admin_notes?: string;
  contact_person?: string;
  contact_email?: string;
  contact_phone?: string;
  company_website?: string;
  company_address?: string;
  created_at: string;
  updated_at: string;
}

interface AdvertisersManagementProps {
  isSpanish?: boolean;
}

const AdvertisersManagement = ({ isSpanish = false }: AdvertisersManagementProps) => {
  const navigate = useNavigate();
  const [ads, setAds] = useState<Ad[]>([]);
  const [selectedAd, setSelectedAd] = useState<Ad | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [placementFilter, setPlacementFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [metrics, setMetrics] = useState({
    totalAds: 0,
    activeAds: 0,
    totalImpressions: 0,
    totalRevenue: 0
  });
  const { toast } = useToast();
  const { getQualityMultipliers } = useImageQuality();

  const [formData, setFormData] = useState({
    ad_name: "",
    company_name: "",
    page_placement: "",
    start_date: new Date(),
    end_date: new Date(),
    cta_url: "",
    offer_text: "",
    estimated_budget: "",
    cost_per_impression: "",
    cost_per_click: "",
    target_locations: [] as string[],
    contact_person: "",
    contact_email: "",
    contact_phone: "",
    company_website: "",
    company_address: "",
    admin_notes: "",
    is_active: true
  });

  const placements = [
    { value: "dashboard", label: isSpanish ? "Panel Principal" : "Dashboard" },
    { value: "checkin", label: isSpanish ? "Pantalla Check-in" : "Check-in Screen" },
    { value: "map", label: isSpanish ? "Vista de Mapa" : "Map View" },
    { value: "sidebar", label: isSpanish ? "Barra Lateral" : "Sidebar" },
    { value: "header", label: isSpanish ? "Encabezado" : "Header" },
    { value: "footer", label: isSpanish ? "Pie de Página" : "Footer" }
  ];

  useEffect(() => {
    fetchAds();
    fetchMetrics();
  }, []);

  const fetchAds = async () => {
    try {
      const { data, error } = await supabase
        .from('ads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAds(data || []);
    } catch (error) {
      console.error('Error fetching ads:', error);
      toast({
        title: isSpanish ? "Error" : "Error",
        description: isSpanish ? "Error al cargar anuncios" : "Error loading ads",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async () => {
    try {
      const { count: totalAds } = await supabase
        .from('ads')
        .select('*', { count: 'exact', head: true });

      const { count: activeAds } = await supabase
        .from('ads')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      const { data: impressionsData } = await supabase
        .from('ads')
        .select('impressions');

      const totalImpressions = impressionsData?.reduce((sum, ad) => sum + (ad.impressions || 0), 0) || 0;

      const { data: revenueData } = await supabase
        .from('advertiser_payments')
        .select('amount');

      const totalRevenue = revenueData?.reduce((sum, payment) => sum + (payment.amount || 0), 0) || 0;

      setMetrics({
        totalAds: totalAds || 0,
        activeAds: activeAds || 0,
        totalImpressions,
        totalRevenue
      });
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
  };

  const columns = [
    {
      id: 'company_name',
      label: isSpanish ? 'Empresa' : 'Company',
      accessor: (ad: Ad) => ad.company_name,
      sortable: true
    },
    {
      id: 'ad_name',
      label: isSpanish ? 'Nombre del Anuncio' : 'Ad Name',
      accessor: (ad: Ad) => ad.ad_name || '-',
      sortable: true
    },
    {
      id: 'placement',
      label: isSpanish ? 'Ubicación' : 'Placement',
      accessor: (ad: Ad) => (
        <Badge variant="outline">{ad.page_placement}</Badge>
      )
    },
    {
      id: 'dates',
      label: isSpanish ? 'Fechas' : 'Dates',
      accessor: (ad: Ad) => (
        <div className="text-sm">
          <div>{format(new Date(ad.start_date), 'MMM d, yyyy')}</div>
          <div className="text-muted-foreground">{format(new Date(ad.end_date), 'MMM d, yyyy')}</div>
        </div>
      )
    },
    {
      id: 'status',
      label: isSpanish ? 'Estado' : 'Status',
      accessor: (ad: Ad) => (
        <Badge variant={ad.is_active ? "default" : "secondary"}>
          {ad.is_active ? (isSpanish ? 'Activo' : 'Active') : (isSpanish ? 'Inactivo' : 'Inactive')}
        </Badge>
      ),
      sortable: true
    },
    {
      id: 'performance',
      label: isSpanish ? 'Rendimiento' : 'Performance',
      accessor: (ad: Ad) => (
        <div className="text-sm space-y-1">
          <div>{ad.impressions.toLocaleString()} {isSpanish ? 'impresiones' : 'impressions'}</div>
          <div className="text-muted-foreground">{ad.clicks} clicks ({ad.ctr.toFixed(2)}%)</div>
        </div>
      )
    },
    {
      id: 'spent',
      label: isSpanish ? 'Gastado' : 'Spent',
      accessor: (ad: Ad) => `$${ad.amount_spent.toFixed(2)}`,
      sortable: true
    }
  ];

  const filteredAds = ads.filter(ad => {
    const matchesSearch = ad.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         ad.ad_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         ad.offer_text?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPlacement = placementFilter === 'all' || ad.page_placement === placementFilter;
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' ? ad.is_active : !ad.is_active);
    return matchesSearch && matchesPlacement && matchesStatus;
  });

  const bulkActions = [
    {
      id: 'activate',
      label: isSpanish ? 'Activar' : 'Activate',
      icon: Play,
      action: async () => {
        try {
          await supabase
            .from('ads')
            .update({ is_active: true })
            .in('id', selectedIds);
          toast({ title: isSpanish ? 'Anuncios activados' : 'Ads activated' });
          fetchAds();
          setSelectedIds([]);
        } catch (error) {
          toast({ title: 'Error', variant: 'destructive' });
        }
      }
    },
    {
      id: 'deactivate',
      label: isSpanish ? 'Desactivar' : 'Deactivate',
      icon: Pause,
      action: async () => {
        try {
          await supabase
            .from('ads')
            .update({ is_active: false })
            .in('id', selectedIds);
          toast({ title: isSpanish ? 'Anuncios desactivados' : 'Ads deactivated' });
          fetchAds();
          setSelectedIds([]);
        } catch (error) {
          toast({ title: 'Error', variant: 'destructive' });
        }
      }
    },
    {
      id: 'delete',
      label: isSpanish ? 'Eliminar' : 'Delete',
      icon: Trash2,
      variant: 'destructive' as const,
      action: async () => {
        if (confirm(isSpanish ? '¿Eliminar anuncios seleccionados?' : 'Delete selected ads?')) {
          try {
            await supabase
              .from('ads')
              .delete()
              .in('id', selectedIds);
            toast({ title: isSpanish ? 'Anuncios eliminados' : 'Ads deleted' });
            fetchAds();
            setSelectedIds([]);
            setSelectedAd(null);
          } catch (error) {
            toast({ title: 'Error', variant: 'destructive' });
          }
        }
      }
    },
    {
      id: 'export',
      label: isSpanish ? 'Exportar' : 'Export',
      icon: Download,
      action: () => {
        const selectedAdsData = ads.filter(ad => selectedIds.includes(ad.id));
        const csv = [
          ['Company', 'Ad Name', 'Placement', 'Status', 'Impressions', 'Clicks', 'CTR', 'Spent'].join(','),
          ...selectedAdsData.map(ad => [
            ad.company_name,
            ad.ad_name || '',
            ad.page_placement,
            ad.is_active ? 'Active' : 'Inactive',
            ad.impressions,
            ad.clicks,
            ad.ctr.toFixed(2),
            ad.amount_spent.toFixed(2)
          ].join(','))
        ].join('\n');
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ads_export_${Date.now()}.csv`;
        a.click();
        setSelectedIds([]);
      }
    }
  ];

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: isSpanish ? "Error" : "Error",
          description: isSpanish 
            ? "La imagen debe ser menor a 10MB (se comprimirá automáticamente)" 
            : "Image must be less than 10MB (will be automatically compressed)",
          variant: "destructive"
        });
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const uploadImageToSupabase = async (file: File): Promise<string> => {
    // Compress the ad image before upload with user quality preference
    const compressedBlob = await compressImage(file, 'ad-banner', undefined, getQualityMultipliers());
    
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const filePath = `ad-images/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('ad-images')
      .upload(filePath, compressedBlob, {
        contentType: file.type
      });

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from('ad-images')
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const handleCreateAd = async () => {
    try {
      setUploading(true);

      if (!imageFile) {
        toast({
          title: isSpanish ? "Error" : "Error",
          description: isSpanish ? "Por favor selecciona una imagen" : "Please select an image",
          variant: "destructive"
        });
        return;
      }

      const imageUrl = await uploadImageToSupabase(imageFile);

      const { error } = await supabase
        .from('ads')
        .insert({
          ad_name: formData.ad_name,
          company_name: formData.company_name,
          image_url: imageUrl,
          page_placement: formData.page_placement,
          start_date: format(formData.start_date, 'yyyy-MM-dd'),
          end_date: format(formData.end_date, 'yyyy-MM-dd'),
          cta_url: formData.cta_url,
          offer_text: formData.offer_text,
          estimated_budget: parseFloat(formData.estimated_budget) || null,
          cost_per_impression: parseFloat(formData.cost_per_impression) || null,
          cost_per_click: parseFloat(formData.cost_per_click) || null,
          target_locations: formData.target_locations,
          contact_person: formData.contact_person,
          contact_email: formData.contact_email,
          contact_phone: formData.contact_phone,
          company_website: formData.company_website,
          company_address: formData.company_address,
          admin_notes: formData.admin_notes,
          is_active: formData.is_active
        });

      if (error) throw error;

      toast({
        title: isSpanish ? "Éxito" : "Success",
        description: isSpanish ? "Anuncio creado exitosamente" : "Ad created successfully"
      });

      setShowCreateDialog(false);
      resetForm();
      fetchAds();
    } catch (error) {
      console.error('Error creating ad:', error);
      toast({
        title: isSpanish ? "Error" : "Error",
        description: isSpanish ? "Error al crear anuncio" : "Error creating ad",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      ad_name: "",
      company_name: "",
      page_placement: "",
      start_date: new Date(),
      end_date: new Date(),
      cta_url: "",
      offer_text: "",
      estimated_budget: "",
      cost_per_impression: "",
      cost_per_click: "",
      target_locations: [],
      contact_person: "",
      contact_email: "",
      contact_phone: "",
      company_website: "",
      company_address: "",
      admin_notes: "",
      is_active: true
    });
    setImageFile(null);
    setImagePreview("");
  };

  return (
    <div className="space-y-6">
      {/* Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {isSpanish ? 'Total de Anuncios' : 'Total Ads'}
            </CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalAds}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {isSpanish ? 'Anuncios Activos' : 'Active Ads'}
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.activeAds}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {isSpanish ? 'Impresiones Totales' : 'Total Impressions'}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalImpressions.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {isSpanish ? 'Ingresos Totales' : 'Total Revenue'}
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${metrics.totalRevenue.toFixed(2)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={isSpanish ? "Buscar anuncios..." : "Search ads..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={placementFilter} onValueChange={setPlacementFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={isSpanish ? "Ubicación" : "Placement"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{isSpanish ? 'Todas' : 'All'}</SelectItem>
              {placements.map(p => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={isSpanish ? "Estado" : "Status"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{isSpanish ? 'Todos' : 'All'}</SelectItem>
              <SelectItem value="active">{isSpanish ? 'Activos' : 'Active'}</SelectItem>
              <SelectItem value="inactive">{isSpanish ? 'Inactivos' : 'Inactive'}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {isSpanish ? 'Nuevo Anuncio' : 'New Ad'}
        </Button>
      </div>

      {/* Bulk Actions */}
      <BulkActions
        selectedCount={selectedIds.length}
        actions={bulkActions}
        onClearSelection={() => setSelectedIds([])}
        isSpanish={isSpanish}
      />

      {/* Table with Split Pane */}
      <SplitPane
        left={
          <EnhancedTable
            data={filteredAds}
            columns={columns}
            onRowClick={(ad) => setSelectedAd(ad as Ad)}
            selectable
            rowKey="id"
          />
        }
        right={
          selectedAd ? (
            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{isSpanish ? 'Detalles del Anuncio' : 'Ad Details'}</h3>
                <Button variant="ghost" size="sm" onClick={() => setSelectedAd(null)}>×</Button>
              </div>

              {selectedAd.image_url && (
                <img 
                  src={selectedAd.image_url} 
                  alt={selectedAd.company_name}
                  className="w-full h-48 object-cover rounded-lg"
                />
              )}

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium mb-1">{isSpanish ? 'Empresa' : 'Company'}</h4>
                  <p className="text-sm text-muted-foreground">{selectedAd.company_name}</p>
                </div>

                {selectedAd.ad_name && (
                  <div>
                    <h4 className="font-medium mb-1">{isSpanish ? 'Nombre del Anuncio' : 'Ad Name'}</h4>
                    <p className="text-sm text-muted-foreground">{selectedAd.ad_name}</p>
                  </div>
                )}

                {selectedAd.offer_text && (
                  <div>
                    <h4 className="font-medium mb-1">{isSpanish ? 'Texto de Oferta' : 'Offer Text'}</h4>
                    <p className="text-sm text-muted-foreground">{selectedAd.offer_text}</p>
                  </div>
                )}

                <div>
                  <h4 className="font-medium mb-1">{isSpanish ? 'Rendimiento' : 'Performance'}</h4>
                  <div className="text-sm space-y-1">
                    <div>{selectedAd.impressions.toLocaleString()} {isSpanish ? 'impresiones' : 'impressions'}</div>
                    <div>{selectedAd.clicks} clicks</div>
                    <div>CTR: {selectedAd.ctr.toFixed(2)}%</div>
                    <div>{isSpanish ? 'Gastado' : 'Spent'}: ${selectedAd.amount_spent.toFixed(2)}</div>
                  </div>
                </div>

                {selectedAd.contact_person && (
                  <div>
                    <h4 className="font-medium mb-1">{isSpanish ? 'Contacto' : 'Contact'}</h4>
                    <div className="text-sm space-y-1 text-muted-foreground">
                      <div>{selectedAd.contact_person}</div>
                      {selectedAd.contact_email && <div>{selectedAd.contact_email}</div>}
                      {selectedAd.contact_phone && <div>{selectedAd.contact_phone}</div>}
                    </div>
                  </div>
                )}

                {selectedAd.admin_notes && (
                  <div>
                    <h4 className="font-medium mb-1">{isSpanish ? 'Notas del Admin' : 'Admin Notes'}</h4>
                    <p className="text-sm text-muted-foreground">{selectedAd.admin_notes}</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              {isSpanish ? 'Selecciona un anuncio para ver detalles' : 'Select an ad to view details'}
            </div>
          )
        }
      />

      {/* Create Dialog - simplified version */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isSpanish ? 'Crear Nuevo Anuncio' : 'Create New Ad'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{isSpanish ? 'Nombre de la Empresa' : 'Company Name'} *</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>{isSpanish ? 'Imagen del Anuncio' : 'Ad Image'} *</Label>
              <Input type="file" accept="image/*" onChange={handleImageUpload} />
              {imagePreview && (
                <ResponsiveImage 
                  src={imagePreview} 
                  alt="Preview" 
                  width={128}
                  height={128}
                  className="mt-2 h-32 rounded"
                  sizes="128px"
                />
              )}
            </div>
            <div>
              <Label>{isSpanish ? 'URL del CTA' : 'CTA URL'} *</Label>
              <Input
                value={formData.cta_url}
                onChange={(e) => setFormData({ ...formData, cta_url: e.target.value })}
                placeholder="https://..."
                required
              />
            </div>
            <Button onClick={handleCreateAd} disabled={uploading} className="w-full">
              {uploading ? (isSpanish ? 'Creando...' : 'Creating...') : (isSpanish ? 'Crear Anuncio' : 'Create Ad')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdvertisersManagement;
